import { useEffect, useState } from "react";
import { Alert, Skeleton } from "@mantine/core";
import { IconUserCheck, IconUsers, IconUsersGroup } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import CheckVerification from "@/components/checkVerificationModal";
import { Customer } from "@/models/response/customer";
import useGetCustomerCount from "@/modules/home/customerCount/hooks";
import SelectUsers from "@/modules/messages/selectUsers";
import { useSmartAudiences } from "@/services/smartAudience/hooks";
import useDiscountStore, { DiscountGroupType } from "@/store/discount";
import CampaignPage, { CampaignActions } from "@/modules/marketing/CampaignPage";
import AudienceGroups, { AudienceSelection } from "@/modules/marketing/AudienceGroups";
import classes from "@/modules/marketing/campaign.module.css";

type Mode = "custom" | "group" | "all";

// Who gets the discount: chosen customers, a group (smart audiences first) or everyone — the same three
// boxes as the plain SMS, opening on «گروه مشتریان».
export function DiscountSelectGroup() {
  const store = useDiscountStore();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>(store.groupType === DiscountGroupType.all ? "all" : store.groupType === DiscountGroupType.customers ? "custom" : "group");
  const [customers, setCustomers] = useState<Customer[]>(store.customers ?? []);
  const count = useGetCustomerCount();
  // A home hint can open this flow as ?audience=<key>[&member=<id>]: that smart audience is chosen.
  const [params, setParams] = useSearchParams();
  const audienceKey = params.get("audience");
  const member = Number(params.get("member")) || undefined;
  const audiences = useSmartAudiences({ merchantUserId: member, enabled: Boolean(audienceKey) });
  const [selected, setSelected] = useState<AudienceSelection | undefined>(store.groupType === DiscountGroupType.smartAudience && store.smartAudience ? { kind: "smart", audience: store.smartAudience, count: store.smartAudience.customerCount ?? 0 } : store.groupType === DiscountGroupType.customerGroup && store.customerGroup ? { kind: "customer", group: store.customerGroup, count: store.count ?? 0 } : store.groupType === DiscountGroupType.discountGroup && store.discountGroup ? { kind: "special", group: store.discountGroup, count: store.count ?? 0 } : undefined);

  useEffect(() => {
    if (!audienceKey || audiences.isLoading) return;
    const audience = audiences.data?.find((item) => item.key === audienceKey);
    setParams({}, { replace: true });
    if (audience) {
      store.setSmartAudience({ id: uuidv4(), smartAudience: { ...audience, merchantUserId: member } });
      setMode("group");
      setSelected({ kind: "smart", audience, count: audience.customerCount ?? 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceKey, audiences.isLoading, audiences.data]);

  const allCount = Number(count.data?.allCount ?? 0);
  const valid = mode === "all" ? allCount > 0 : mode === "custom" ? customers.length > 0 : Boolean(selected?.count);
  const next = () => {
    if (!valid) return;
    const id = store.id ?? uuidv4();
    if (mode === "all") store.setCustomers({ id, all: true, customers: [], count: allCount });
    else if (mode === "custom") store.setCustomers({ id, all: false, customers, count: customers.length });
    else if (selected?.kind === "customer") store.addCustomerGroup({ id, customerGroup: selected.group, count: selected.count });
    else if (selected?.kind === "smart") store.setSmartAudience({ id, smartAudience: store.smartAudience && store.smartAudience.key === selected.audience.key ? store.smartAudience : selected.audience });
    else if (selected?.kind === "special") store.addDiscountGroup({ id, discountGroup: selected.group, count: selected.count });
    navigate("/dashboard/discount/selectMessage");
  };

  const modes = [
    { value: "custom" as const, title: "انتخاب مشتری", text: "چند مشتری مشخص", Icon: IconUserCheck },
    { value: "group" as const, title: "گروه مشتریان", text: "پیشنهاد هوشمند یا گروه‌های شما", Icon: IconUsersGroup },
    { value: "all" as const, title: "همهٔ مشتریان", text: "تمام فهرست مشتریان شما", Icon: IconUsers },
  ];

  return (
    <CampaignPage flow="discount" step={0} title="یک پیشنهاد خوب، برای مخاطب درست" description="مخاطبانی را انتخاب کنید که کد تخفیف را دریافت می‌کنند."
      actions={<CampaignActions next="ادامه؛ انتخاب متن" onNext={next} disabled={!valid} hint={valid ? "مخاطبان انتخاب شدند" : "برای ادامه، مخاطب انتخاب کنید"} />}>
      <div className={classes.choiceGrid}>
        {modes.map(({ value, title, text, Icon }) => (
          <button type="button" key={value} className={classes.choice} aria-pressed={mode === value} onClick={() => setMode(value)}>
            <Icon size={24} /><strong>{title}</strong><small>{text}</small>
          </button>
        ))}
      </div>
      {mode === "custom" && <SelectUsers selectedItems={customers} setSelectedItems={setCustomers} />}
      {mode === "group" && <AudienceGroups value={selected} onChange={setSelected} withSmart />}
      {mode === "all" && (
        <>
          <Alert color="violet" title="کد تخفیف برای همهٔ مشتریان">هر مشتری یک کد یک‌بارمصرف می‌گیرد. هزینهٔ نهایی پیش از ارسال نمایش داده می‌شود.</Alert>
          {count.isLoading ? <Skeleton height={60} mt="md" /> : <div className={classes.selection}><span>تعداد مشتریان</span><strong>{count.data?.allCount !== undefined ? allCount.toLocaleString("fa-IR") : "—"} نفر</strong></div>}
        </>
      )}
      <CheckVerification />
    </CampaignPage>
  );
}
